import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowUp, CookingPot, LayoutGrid, List, SearchX, WifiOff } from 'lucide-react';
import { TAGS, isTagSlug, type RecipeSummary, type TagSlug } from '@dishly/shared';
import { EmptyState, SegmentedControl } from '../components/common';
import { RecipeCard, RecipeRow } from '../components/RecipeCard';
import { useOnline } from '../lib/browser';
import { useSyncStatus } from '../lib/sync';
import { useData } from '../stores/data';
import { usePrefs, type ViewMode } from '../stores/prefs';
import { useSession } from '../stores/session';

const PAGE_SIZE = 24;
const SORTS = ['newest', 'rating', 'quickest', 'name'] as const;
type SortKey = (typeof SORTS)[number];

/** Ranks by rating, but pulls small samples towards an average score (one 5★ shouldn't beat fifty 4.8★). */
function ratingScore(recipe: RecipeSummary): number {
  return (recipe.ratingAvg * recipe.ratingCount + 3 * 2) / (recipe.ratingCount + 2);
}

const COMPARATORS: Record<SortKey, (a: RecipeSummary, b: RecipeSummary) => number> = {
  newest: (a, b) => (b.publishedAt ?? b.createdAt).localeCompare(a.publishedAt ?? a.createdAt),
  rating: (a, b) => ratingScore(b) - ratingScore(a) || b.ratingCount - a.ratingCount,
  quickest: (a, b) => (a.totalTimeMinutes ?? Infinity) - (b.totalTimeMinutes ?? Infinity) || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
};

function storedCount(key: string): number {
  return Number(sessionStorage.getItem(`dishly-home:${key}`)) || PAGE_SIZE;
}

function TagFilter({
  active,
  counts,
  onToggle,
  onClear,
}: {
  active: TagSlug[];
  counts: Record<TagSlug, number>;
  onToggle: (tag: TagSlug) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('home.tags')} className="-mx-4 lg:mx-0">
      <h2 className="mb-3 hidden text-xs font-bold tracking-wider text-muted uppercase lg:block">{t('home.tags')}</h2>
      <ul className="flex gap-2 overflow-x-auto px-4 pb-2 [scrollbar-width:none] lg:flex-col lg:overflow-visible lg:px-0">
        <li>
          <button type="button" className="chip whitespace-nowrap lg:w-full" aria-pressed={active.length === 0} onClick={onClear}>
            {t('home.showAll')}
          </button>
        </li>
        {TAGS.map((tag) => {
          const on = active.includes(tag);
          return (
            <li key={tag}>
              <button
                type="button"
                className="chip whitespace-nowrap lg:w-full lg:justify-between"
                aria-pressed={on}
                onClick={() => onToggle(tag)}
              >
                {t(`tags.${tag}`)}
                {!on && <span className="text-xs text-muted tabular-nums">{counts[tag]}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-5" aria-hidden>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="overflow-hidden rounded-3xl border border-line bg-surface">
          <div className="aspect-[4/3] animate-pulse bg-surface-2" />
          <div className="space-y-2 p-4">
            <div className="h-5 w-3/4 animate-pulse rounded bg-surface-2" />
            <div className="h-4 w-1/3 animate-pulse rounded bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function BackToTop() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 1200);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="animate-pop-in fixed end-4 bottom-4 z-30 flex size-12 items-center justify-center rounded-full border border-line bg-surface shadow-lg hover:bg-surface-2"
      aria-label={t('home.backToTop')}
      title={t('home.backToTop')}
    >
      <ArrowUp className="size-5" />
    </button>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const view = usePrefs((s) => s.view);
  const summaries = useData((s) => s.summaries);
  const loaded = useData((s) => s.summariesLoaded);
  const { syncing, hasSynced } = useSyncStatus();
  const user = useSession((s) => s.user);
  const online = useOnline();

  const activeTags = useMemo(() => params.get('tags')?.split(',').filter(isTagSlug) ?? [], [params]);
  const sortParam = params.get('sort');
  const sort: SortKey = SORTS.includes(sortParam as SortKey) ? (sortParam as SortKey) : 'newest';

  const published = useMemo(() => summaries.filter((r) => !r.isDraft && r.visibility === 'public'), [summaries]);
  const filtered = useMemo(
    () => published.filter((r) => activeTags.every((tag) => r.tags.includes(tag))).sort(COMPARATORS[sort]),
    [published, activeTags, sort],
  );
  const counts = useMemo(
    () => Object.fromEntries(TAGS.map((tag) => [tag, filtered.filter((r) => r.tags.includes(tag)).length])) as Record<TagSlug, number>,
    [filtered],
  );

  // Infinite scroll over the locally synced recipes. The rendered count is
  // remembered per filter so going back to the list restores the position.
  const filterKey = `${activeTags.join(',')}|${sort}`;
  const [paging, setPaging] = useState(() => ({ key: filterKey, count: storedCount(filterKey) }));
  const count = paging.key === filterKey ? paging.count : PAGE_SIZE;
  const hasMore = filtered.length > count;
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    sessionStorage.setItem(`dishly-home:${filterKey}`, String(count));
  }, [filterKey, count]);

  useEffect(() => {
    const target = sentinel.current;
    if (!target || !hasMore) return;
    // Re-created after each page so it fires again if the sentinel is still in view.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setPaging({ key: filterKey, count: count + PAGE_SIZE });
      },
      { rootMargin: '800px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [filterKey, count, hasMore]);

  const updateParams = (tags: TagSlug[], nextSort: SortKey) => {
    const next = new URLSearchParams();
    if (tags.length) next.set('tags', tags.join(','));
    if (nextSort !== 'newest') next.set('sort', nextSort);
    setParams(next, { replace: true, preventScrollReset: true });
  };
  const toggleTag = (tag: TagSlug) =>
    updateParams(activeTags.includes(tag) ? activeTags.filter((t) => t !== tag) : [...activeTags, tag], sort);

  const firstLoad = !loaded || (summaries.length === 0 && !hasSynced && syncing);

  let content;
  if (firstLoad) {
    content = <SkeletonGrid />;
  } else if (published.length === 0) {
    content =
      !online && !hasSynced ? (
        <EmptyState icon={<WifiOff className="size-7" />} title={t('home.offlineEmpty')} />
      ) : (
        <EmptyState
          icon={<CookingPot className="size-7" />}
          title={t('home.empty')}
          action={
            <Link to={user ? '/new' : '/login?next=%2Fnew'} className="btn-primary">
              {t('nav.addRecipe')}
            </Link>
          }
        >
          {t('home.emptyHint')}
        </EmptyState>
      );
  } else if (filtered.length === 0) {
    content = (
      <EmptyState icon={<SearchX className="size-7" />} title={t('home.noMatches')}>
        {t('home.noMatchesHint')}
      </EmptyState>
    );
  } else {
    const shown = filtered.slice(0, count);
    content = (
      <>
        {view === 'grid' ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-5">
            {shown.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {shown.map((recipe) => (
              <RecipeRow key={recipe.id} recipe={recipe} />
            ))}
          </div>
        )}
        <div ref={sentinel} />
        {!hasMore && filtered.length > PAGE_SIZE && <p className="py-10 text-center text-sm text-muted">{t('home.end')}</p>}
      </>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 pb-16 sm:px-6 lg:grid lg:grid-cols-[12rem_1fr] lg:gap-10">
      <aside className="lg:sticky lg:top-6 lg:self-start">
        <TagFilter active={activeTags} counts={counts} onToggle={toggleTag} onClear={() => updateParams([], sort)} />
      </aside>

      <section aria-labelledby="recipes-heading" className="min-w-0">
        <div className="mt-4 mb-5 flex flex-wrap items-end gap-3 lg:mt-0">
          <div className="me-auto">
            <h1 id="recipes-heading" className="font-display text-3xl font-bold tracking-tight">
              {activeTags.length ? activeTags.map((tag) => t(`tags.${tag}`)).join(' + ') : t('home.title')}
            </h1>
            {!firstLoad && <p className="text-sm text-muted">{t('home.count', { count: filtered.length })}</p>}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t('home.sortLabel')}</span>
            <select
              className="rounded-full border border-line bg-surface px-3 py-1.5 font-semibold"
              value={sort}
              onChange={(event) => updateParams(activeTags, event.target.value as SortKey)}
            >
              {SORTS.map((key) => (
                <option key={key} value={key}>
                  {t(`home.sort.${key}`)}
                </option>
              ))}
            </select>
          </label>
          <SegmentedControl<ViewMode>
            label={t('home.viewGrid')}
            value={view}
            onChange={(value) => usePrefs.setState({ view: value })}
            options={[
              { value: 'grid', label: <LayoutGrid className="size-4" aria-label={t('home.viewGrid')} /> },
              { value: 'list', label: <List className="size-4" aria-label={t('home.viewList')} /> },
            ]}
          />
        </div>
        {content}
      </section>
      <BackToTop />
    </div>
  );
}
